import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React from 'react';

import { MyReportsSortModel } from './myReportsListHelpers';

/**
 * Sizing follows the shared V4 list-table model in `configs/tableStyles`:
 * either a fixed pixel `width`, or a `flex` weight with a `minWidth` floor.
 * The table's `<colgroup>` applies the resulting widths — the cells themselves
 * carry none, because under `table-layout: fixed` a cell width is dead style.
 */
export interface MyReportsColumn {
  key: string;
  label: string;
  sortable?: boolean;
  width?: number;
  flex?: number;
  minWidth?: number;
}

interface MyReportsTableHeadProps {
  columns: MyReportsColumn[];
  sortModel: MyReportsSortModel;
  onSortChange: (model: MyReportsSortModel) => void;
}

const HEADER_SX = {
  fontSize: '10px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  fontWeight: 600,
  whiteSpace: 'nowrap' as const,
  py: 0.75,
  px: 1,
  borderBottom: '2px solid',
  borderColor: 'v4.border',
} as const;

export const MyReportsTableHead: React.FC<MyReportsTableHeadProps> = ({
  columns,
  sortModel,
  onSortChange,
}) => {
  const theme = useTheme();

  const handleSort = (field: string) => {
    if (sortModel.field !== field) onSortChange({ field, direction: 'asc' });
    else if (sortModel.direction === 'asc') onSortChange({ field, direction: 'desc' });
    else onSortChange({ field: 'name', direction: 'asc' });
  };

  return (
    <TableHead>
      <TableRow sx={{ bgcolor: theme.palette.v4.tableEven }}>
        {columns.map((col) => {
          const isSorted = sortModel.field === col.key;
          const ariaSort = isSorted
            ? sortModel.direction === 'asc'
              ? 'ascending'
              : 'descending'
            : col.sortable
              ? 'none'
              : undefined;

          return (
            <TableCell
              key={col.key}
              sx={{
                ...HEADER_SX,
                color: isSorted ? theme.palette.v4.accent : 'text.secondary',
              }}
              aria-sort={ariaSort}
            >
              {col.sortable ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => handleSort(col.key)}
                  aria-label={`Sort by ${col.label}, ${ariaSort}`}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    color: 'inherit',
                    letterSpacing: 'inherit',
                    textTransform: 'inherit',
                    fontWeight: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  {col.label}
                  {isSorted &&
                    (sortModel.direction === 'asc' ? (
                      <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                    ) : (
                      <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                    ))}
                </Box>
              ) : (
                <Box component="span">{col.label}</Box>
              )}
            </TableCell>
          );
        })}
        <TableCell
          sx={{
            ...HEADER_SX,
            color: 'text.secondary',
            position: 'sticky',
            right: 0,
            bgcolor: theme.palette.v4.tableEven,
            zIndex: 2,
          }}
          aria-label="Actions"
        />
      </TableRow>
    </TableHead>
  );
};
